import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Send, AlertTriangle, Trash2 } from "lucide-react";
import { HeaderAccent } from "@/lib/status";

const PRIORITY = {
  urgent: "bg-coral-light text-coral-dark border border-coral/40",
  important: "bg-gold-light text-gold-dark border border-gold/40",
  normal: "bg-slate-100 text-slate-500 border border-slate-200",
};

export default function Inbox() {
  const { user } = useAuth();
  const isManager = ["admin", "office"].includes(user?.role);
  const [tab, setTab] = useState("inbox");
  const [msgs, setMsgs] = useState([]);
  const [sent, setSent] = useState([]);
  const [open, setOpen] = useState(null);
  const [compose, setCompose] = useState(false);
  const [replyPreset, setReplyPreset] = useState(null);

  const load = () => api.get("/messages").then((r) => setMsgs(r.data)).catch(() => {});
  const loadSent = () => api.get("/messages/sent").then((r) => setSent(r.data)).catch(() => {});
  useEffect(() => { load(); loadSent(); }, []);

  const openMsg = (m) => {
    setOpen(m);
    if (tab === "inbox" && !m.read) api.post(`/messages/${m.id}/read`).then(() => { setMsgs((l) => l.map((x) => (x.id === m.id ? { ...x, read: true } : x))); }).catch(() => {});
  };

  const delMsg = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/messages/${id}`);
      setMsgs((l) => l.filter((x) => x.id !== id));
      setSent((l) => l.filter((x) => x.id !== id));
      if (open?.id === id) setOpen(null);
      toast.success("Message deleted");
    } catch (err) { toast.error(apiError(err)); }
  };

  const rows = tab === "inbox" ? msgs : sent;
  const tabBtn = (key, label, count) => (
    <button onClick={() => { setTab(key); setOpen(null); }} data-testid={`inbox-tab-${key}`}
      className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === key ? "bg-brand text-white" : "text-ink-soft hover:bg-secondary"}`}>
      {label}{count > 0 && <span className="ml-1.5 text-xs opacity-80">({count})</span>}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Inbox</h1>
          <p className="mt-1 text-ink-soft">Messages to and from the office. Urgent &amp; important ones must be read before you can check in.</p>
          <HeaderAccent />
        </div>
        <button onClick={() => setCompose(true)} data-testid="compose-message-button"
          className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark">
          <Send size={18} /> New message
        </button>
      </div>

      <div className="flex gap-2" data-testid="inbox-tabs">
        {tabBtn("inbox", "Received", msgs.filter((m) => !m.read).length)}
        {tabBtn("sent", "Sent", 0)}
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
        {rows.length === 0 && <p className="p-8 text-center text-ink-muted">{tab === "inbox" ? "No messages yet." : "You haven't sent any messages yet."}</p>}
        {rows.map((m) => (
          <div key={m.id} className={`group flex w-full items-center gap-3 border-b border-border px-5 py-4 last:border-0 hover:bg-secondary ${tab === "inbox" && !m.read ? "bg-brand-light/40" : ""}`}>
            <button onClick={() => openMsg(m)} data-testid={`message-${m.id}`} className="flex min-w-0 flex-1 items-center gap-3 text-left">
              {tab === "inbox" && !m.read && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-brand" />}
              <div className="min-w-0 flex-1">
                <p className={`truncate ${tab === "inbox" && !m.read ? "font-bold text-ink" : "font-medium text-ink"}`}>{m.subject}</p>
                <p className="truncate text-xs text-ink-muted">
                  {tab === "inbox" ? `From ${m.sender_name}` : `To ${m.recipient_name}`} · {new Date(m.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  {tab === "sent" && (m.read ? " · Read" : " · Unread")}
                </p>
              </div>
              {m.priority !== "normal" && (
                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${PRIORITY[m.priority]}`}>{m.priority}</span>
              )}
            </button>
            <button onClick={(e) => delMsg(e, m.id)} data-testid={`delete-message-${m.id}`} title="Delete message"
              className="shrink-0 rounded-full p-1.5 text-ink-muted opacity-0 transition-opacity hover:bg-coral-light hover:text-coral group-hover:opacity-100">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(null)} />
          <div data-testid="message-detail-modal" className="relative z-10 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-2 flex items-start justify-between gap-3">
              <h2 className="font-heading text-xl font-bold text-ink">{open.subject}</h2>
              <button onClick={() => setOpen(null)}><X size={20} className="text-ink-muted" /></button>
            </div>
            {open.priority !== "normal" && (
              <span className={`mb-3 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${PRIORITY[open.priority]}`}><AlertTriangle size={12} /> {open.priority}</span>
            )}
            <p className="mb-4 text-xs text-ink-muted">{tab === "inbox" ? `From ${open.sender_name}` : `To ${open.recipient_name}`}</p>
            <p className="whitespace-pre-wrap text-sm text-ink">{open.body || "—"}</p>
            <div className="mt-6 flex gap-3">
              <button onClick={(e) => delMsg(e, open.id)} data-testid="message-delete-button"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-coral px-4 py-2.5 font-semibold text-coral hover:bg-coral hover:text-white">
                <Trash2 size={16} /> Delete
              </button>
            {(() => {
              const targetId = tab === "inbox" ? open.sender_id : open.recipient_id;
              const targetName = tab === "inbox" ? open.sender_name : open.recipient_name;
              if (!targetId || targetId === user?.id) return null;
              return (
                <button onClick={() => { setReplyPreset({ recipientId: targetId, recipientName: targetName, subject: open.subject?.startsWith("Re:") ? open.subject : `Re: ${open.subject}` }); setOpen(null); setCompose(true); }}
                  data-testid="message-reply-button" className="inline-flex items-center justify-center gap-2 rounded-lg border border-brand px-4 py-2.5 font-semibold text-brand hover:bg-brand-light">
                  <Send size={16} /> Reply
                </button>
              );
            })()}
              <button onClick={() => setOpen(null)} data-testid="message-ack-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">{tab === "inbox" ? "I've read this" : "Close"}</button>
            </div>
          </div>
        </div>
      )}

      {compose && <Compose isManager={isManager} preset={replyPreset} onClose={() => { setCompose(false); setReplyPreset(null); }} onSent={() => { setCompose(false); setReplyPreset(null); loadSent(); setTab("sent"); }} />}
    </div>
  );
}

function Compose({ isManager, preset, onClose, onSent }) {
  const [recipients, setRecipients] = useState([]);
  const [sel, setSel] = useState(preset?.recipientId ? [preset.recipientId] : []);
  const [subject, setSubject] = useState(preset?.subject || "");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  useEffect(() => { api.get("/message-recipients").then((r) => setRecipients(r.data)).catch(() => {}); }, []);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const filtered = recipients.filter((r) => `${r.name} ${r.role}`.toLowerCase().includes(q.toLowerCase()));

  const send = async () => {
    if (sel.length === 0) { toast.error("Pick at least one recipient"); return; }
    if (!subject.trim()) { toast.error("Add a subject"); return; }
    setBusy(true);
    try { await api.post("/messages", { recipient_ids: sel, subject, body, priority }); toast.success(`Sent to ${sel.length} recipient(s)`); onSent(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div data-testid="compose-modal" className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-ink">New message</h2>
          <button onClick={onClose}><X size={20} className="text-ink-muted" /></button>
        </div>
        {!isManager && <p className="mb-3 rounded-lg bg-brand-light/50 px-3 py-2 text-xs text-ink-soft">Your message goes to the office &amp; admin team.</p>}
        {preset?.recipientName && <p className="mb-3 rounded-lg bg-sage-light/60 px-3 py-2 text-xs font-medium text-sage-dark" data-testid="reply-banner">Replying to {preset.recipientName}</p>}
        <input data-testid="message-subject-input" className={input} placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <textarea rows={4} data-testid="message-body-input" className={`${input} mt-3`} placeholder="Write your message…" value={body} onChange={(e) => setBody(e.target.value)} />
        {isManager && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm font-medium text-ink">Priority:</span>
            {["normal", "important", "urgent"].map((p) => (
              <button key={p} onClick={() => setPriority(p)} data-testid={`priority-${p}`}
                className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${priority === p ? PRIORITY[p] : "bg-slate-100 text-slate-500"}`}>{p}</button>
            ))}
          </div>
        )}
        <div className="mt-4 min-h-0 flex-1 overflow-hidden">
          <input className={`${input} mb-2`} placeholder="Search people…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="max-h-48 overflow-y-auto rounded-lg border border-border" data-testid="recipient-list">
            {filtered.length === 0 && <p className="p-3 text-sm text-ink-muted">No recipients available.</p>}
            {filtered.map((r) => (
              <label key={r.id} className="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-2 text-sm last:border-0 hover:bg-secondary">
                <input type="checkbox" className="h-4 w-4 accent-brand" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`recipient-${r.id}`} />
                <span className="flex-1 text-ink">{r.name}</span>
                <span className="text-xs capitalize text-ink-muted">{r.role === "carer" ? "befriender" : r.role}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
          <button onClick={send} disabled={busy} data-testid="send-message-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{busy ? "Sending…" : "Send"}</button>
        </div>
      </div>
    </div>
  );
}
